/**
 * Weighted risk scoring (DEV-90) — the arithmetic, shared by the gateway that
 * stores an assessment and the console that shows it being filled in, so the
 * number an officer sees is the number that is recorded.
 *
 * A model is categories of criteria. Each criterion carries a weight as a share
 * of the whole (basis points: 5% = 500), so a category's weight is the sum of
 * its criteria and all weights add up to 100%. An officer picks a risk level per
 * criterion; the level carries a score. The total is the weighted mean of those
 * scores; thresholds classify it.
 *
 * Integer arithmetic throughout: weights in basis points and whole-number
 * scores make every sum exact, and the total is rounded half-up to hundredths
 * only for display. Classification compares the exact sum, so rounding can
 * never move a client across a threshold.
 */

export const RISK_RATINGS = ['LOW', 'MEDIUM', 'HIGH', 'PROHIBITED'] as const;
export type RiskRatingValue = (typeof RISK_RATINGS)[number];

export const FULL_WEIGHT_BP = 10_000;

export interface RiskCriterion {
  readonly id: string;
  readonly name: string;
  /** Share of the whole model, in basis points (5% = 500). */
  readonly weightBp: number;
}

export interface RiskCategory {
  readonly id: string;
  readonly name: string;
  readonly criteria: readonly RiskCriterion[];
}

export interface RiskLevel {
  /** Stable key, e.g. LOW, MEDIUM, HIGH, VERY_HIGH. */
  readonly level: string;
  readonly label: string;
  /** Whole number, 0–100. */
  readonly score: number;
}

export interface RiskThreshold {
  readonly rating: RiskRatingValue;
  /** A total at or above this (in hundredths: 3.50 → 350) classifies as `rating`. */
  readonly minHundredths: number;
}

export interface RiskModelDefinition {
  readonly categories: readonly RiskCategory[];
  readonly levels: readonly RiskLevel[];
  /** Ascending by minHundredths; the first starts at 0 so every total classifies. */
  readonly thresholds: readonly RiskThreshold[];
}

export interface RiskAnswer {
  readonly criterionId: string;
  readonly level: string;
  readonly comment?: string;
}

const MAX_NAME = 120;
const MAX_COMMENT = 2000;

/** Every problem with a model, in words; empty when it may be saved. */
export function validateRiskModel(model: RiskModelDefinition): string[] {
  const problems: string[] = [];
  if (model.categories.length === 0) problems.push('Add at least one category.');
  const ids = new Set<string>();
  let total = 0;
  for (const category of model.categories) {
    const name = category.name.trim();
    if (name === '' || name.length > MAX_NAME) problems.push(`Every category needs a name of up to ${String(MAX_NAME)} characters.`);
    if (ids.has(category.id) || category.id === '') problems.push(`Category "${name}" needs a unique id.`);
    ids.add(category.id);
    if (category.criteria.length === 0) problems.push(`Category "${name}" has no criteria.`);
    for (const criterion of category.criteria) {
      const cname = criterion.name.trim();
      if (cname === '' || cname.length > MAX_NAME) problems.push(`Every criterion in "${name}" needs a name of up to ${String(MAX_NAME)} characters.`);
      if (ids.has(criterion.id) || criterion.id === '') problems.push(`Criterion "${cname}" needs a unique id.`);
      ids.add(criterion.id);
      if (!Number.isInteger(criterion.weightBp) || criterion.weightBp <= 0) {
        problems.push(`Criterion "${cname}" needs a weight above 0%, to at most two decimals.`);
      } else {
        total += criterion.weightBp;
      }
    }
  }
  if (total !== FULL_WEIGHT_BP) {
    problems.push(`The weights add up to ${formatWeight(total)}; they must add up to 100%.`);
  }

  if (model.levels.length < 2) problems.push('Define at least two risk levels.');
  const levels = new Set<string>();
  for (const level of model.levels) {
    if (level.level === '' || levels.has(level.level)) problems.push(`Risk level "${level.label}" needs a unique key.`);
    levels.add(level.level);
    if (level.label.trim() === '') problems.push('Every risk level needs a label.');
    if (!Number.isInteger(level.score) || level.score < 0 || level.score > 100) {
      problems.push(`Risk level "${level.label}" needs a whole-number score from 0 to 100.`);
    }
  }

  if (model.thresholds.length === 0) problems.push('Define how a total is classified.');
  model.thresholds.forEach((threshold, index) => {
    if (!RISK_RATINGS.includes(threshold.rating)) problems.push(`"${threshold.rating}" is not a risk rating.`);
    if (!Number.isInteger(threshold.minHundredths) || threshold.minHundredths < 0) {
      problems.push(`The ${threshold.rating} threshold must be a score of 0 or more.`);
    }
    const previous = model.thresholds[index - 1];
    if (index === 0 && threshold.minHundredths !== 0) problems.push('The lowest classification must start at 0, so every total is classified.');
    if (previous !== undefined && threshold.minHundredths <= previous.minHundredths) {
      problems.push('Thresholds must rise from one classification to the next.');
    }
  });
  if (new Set(model.thresholds.map((t) => t.rating)).size !== model.thresholds.length) {
    problems.push('Each classification may appear once.');
  }
  return problems;
}

export interface ScoredCriterion {
  readonly criterionId: string;
  readonly name: string;
  readonly weightBp: number;
  readonly level: string;
  readonly levelLabel: string;
  readonly score: number;
  readonly comment: string;
}

export interface ScoredCategory {
  readonly categoryId: string;
  readonly name: string;
  readonly weightBp: number;
  /** This category's contribution to the total, in hundredths (rounded half-up). */
  readonly subtotalHundredths: number;
  readonly criteria: readonly ScoredCriterion[];
}

export interface RiskScore {
  readonly categories: readonly ScoredCategory[];
  /** Criteria still without a level. A complete assessment has none. */
  readonly unanswered: readonly string[];
  /** Rounded half-up to hundredths, for display and storage. */
  readonly totalHundredths: number;
  /** From the exact total; null until every criterion is answered. */
  readonly classification: RiskRatingValue | null;
}

/** a / b rounded half-up, for non-negative integers. */
function divideHalfUp(a: number, b: number): number {
  return Math.floor((a * 2 + b) / (b * 2));
}

/** Score answers against a model. Unknown levels and missing answers are reported, not guessed. */
export function scoreRisk(model: RiskModelDefinition, answers: readonly RiskAnswer[]): RiskScore {
  const byCriterion = new Map(answers.map((answer) => [answer.criterionId, answer]));
  const levels = new Map(model.levels.map((level) => [level.level, level]));
  const unanswered: string[] = [];
  let exact = 0; // Σ weightBp × score; the total is exact / 10 000.

  const categories = model.categories.map((category) => {
    let categoryExact = 0;
    const criteria = category.criteria.map((criterion) => {
      const answer = byCriterion.get(criterion.id);
      const level = answer === undefined ? undefined : levels.get(answer.level);
      if (level === undefined) unanswered.push(criterion.id);
      const score = level?.score ?? 0;
      categoryExact += criterion.weightBp * score;
      return {
        criterionId: criterion.id, name: criterion.name, weightBp: criterion.weightBp,
        level: level?.level ?? '', levelLabel: level?.label ?? '', score,
        comment: (answer?.comment ?? '').trim().slice(0, MAX_COMMENT),
      };
    });
    exact += categoryExact;
    return {
      categoryId: category.id, name: category.name,
      weightBp: category.criteria.reduce((sum, criterion) => sum + criterion.weightBp, 0),
      subtotalHundredths: divideHalfUp(categoryExact, 100),
      criteria,
    };
  });

  let classification: RiskRatingValue | null = null;
  if (unanswered.length === 0) {
    for (const threshold of model.thresholds) {
      // exact / 10 000 ≥ min / 100  ⇔  exact ≥ min × 100
      if (exact >= threshold.minHundredths * 100) classification = threshold.rating;
    }
  }
  return { categories, unanswered, totalHundredths: divideHalfUp(exact, 100), classification };
}

/** 500 → "5.00" */
export function formatScore(hundredths: number): string {
  return `${String(Math.floor(hundredths / 100))}.${String(hundredths % 100).padStart(2, '0')}`;
}

/** 3500 → "35%", 250 → "2.5%" */
export function formatWeight(bp: number): string {
  const whole = Math.floor(bp / 100);
  const fraction = bp % 100;
  return fraction === 0 ? `${String(whole)}%` : `${String(whole)}.${String(fraction).padStart(2, '0').replace(/0$/, '')}%`;
}
